import { useCallback, useEffect, useState } from 'react';
import { useOrganization } from 'context/OrganizationContext';
import { Stable, StablesService } from 'openapi';
import { ApiPromises } from 'utilities/ApiPromises';
import useApiPromises from './useApiPromises';

interface ReturnType {
  stables: Stable[] | undefined;
  loading: boolean;
  error: string | undefined;
  refresh: () => void;
}

/**
 * Custom hooks to load the stables
 */
const useStables = (): ReturnType => {
  const [stables, setStables] = useState<Stable[]>();
  const [apiPromises, setApiPromises] = useState<ApiPromises>();

  const { selectedOrganization, generateCacheKey } = useOrganization();
  const { errorString, loading } = useApiPromises({ apiPromises });

  /**
   * Load the horse owners
   */
  const loadApiData = useCallback(
    (organisationUid: string) => {
      const promises = new ApiPromises();

      promises.appendList<Stable>(
        'stables',
        () =>
          StablesService.stablesList({
            organisationUid,
          }),
        setStables,
        generateCacheKey('stables'),
      );

      setApiPromises(promises);

      return promises;
    },
    [generateCacheKey],
  );

  /**
   * Refresh the stables
   */
  const refresh = useCallback(async () => {
    if (!selectedOrganization) return;
    loadApiData(selectedOrganization.uid);
  }, [loadApiData, selectedOrganization]);

  /**
   * Load the Stables
   */
  useEffect(() => {
    if (selectedOrganization) {
      const promise = loadApiData(selectedOrganization.uid);
      return () => promise.cancel();
    }
  }, [loadApiData, selectedOrganization]);

  return {
    stables,
    loading,
    error: errorString,
    refresh,
  };
};

export default useStables;
